// Concurrency-limited image preloader.
//
// The backend image proxy breaks when too many images are requested at once, so every
// image the previewer renders is preloaded through this queue first (max N concurrent
// `new Image()` loads, highest priority first). Entries are deduped by URL — once a URL
// is loaded the browser cache makes any subsequent <img src> render free.

export const STATUS = {
  idle: 'idle',
  queued: 'queued',
  loading: 'loading',
  loaded: 'loaded',
  error: 'error'
};

export const createImageLoader = ({ concurrency = 4, retries = 2, baseDelay = 600 } = {}) => {
  const entries = new Map();
  const queue = [];
  let active = 0;

  const takeNext = () => {
    if (!queue.length) return null;
    let bestIdx = 0;
    for (let i = 1; i < queue.length; i++) {
      if (entries.get(queue[i]).priority > entries.get(queue[bestIdx]).priority) bestIdx = i;
    }
    return queue.splice(bestIdx, 1)[0];
  };

  const pump = () => {
    while (active < concurrency) {
      const url = takeNext();
      if (!url) return;
      const entry = entries.get(url);
      entry.status = STATUS.loading;
      active++;
      const img = new Image();
      img.onload = () => {
        active--;
        entry.status = STATUS.loaded;
        entry.resolve();
        pump();
      };
      img.onerror = () => {
        active--;
        entry.attempts++;
        if (entry.attempts <= retries) {
          // Exponential backoff + jitter before going back into the queue
          const delay = baseDelay * 2 ** (entry.attempts - 1) + Math.random() * 200;
          entry.status = STATUS.queued;
          setTimeout(() => {
            // The entry may have been canceled while waiting
            if (entries.get(url) === entry && entry.status === STATUS.queued) {
              queue.push(url);
              pump();
            }
          }, delay);
        } else {
          entry.status = STATUS.error;
          entry.reject(new Error(`Failed to load image: ${url}`));
        }
        pump();
      };
      img.src = url;
    }
  };

  const enqueue = (url, priority) => {
    const entry = { status: STATUS.queued, priority, attempts: 0 };
    entry.promise = new Promise((resolve, reject) => {
      entry.resolve = resolve;
      entry.reject = reject;
    });
    // Avoid unhandled-rejection noise when nothing awaits a canceled/failed entry
    entry.promise.catch(() => {});
    entries.set(url, entry);
    queue.push(url);
    pump();
    return entry.promise;
  };

  return {
    load(url, { priority = 0 } = {}) {
      if (!url) return Promise.resolve();
      const entry = entries.get(url);
      if (!entry) return enqueue(url, priority);
      // Errors stay sticky (no auto-retry storms against the proxy) — use retry()
      if (entry.status === STATUS.loaded || entry.status === STATUS.error) return entry.promise;
      entry.priority = Math.max(entry.priority, priority);
      return entry.promise;
    },
    retry(url) {
      const entry = entries.get(url);
      if (!entry || entry.status !== STATUS.error) return this.load(url);
      entries.delete(url);
      return enqueue(url, entry.priority);
    },
    getStatus(url) {
      return entries.get(url)?.status || STATUS.idle;
    },
    cancelPending() {
      // Covers both queue members and entries waiting in a retry backoff timeout
      queue.length = 0;
      entries.forEach((entry, url) => {
        if (entry.status !== STATUS.queued) return;
        const error = new Error('Image load canceled');
        error.canceled = true;
        entry.reject(error);
        entries.delete(url);
      });
    }
  };
};

// Shared singleton — the loaded/error cache survives viewer open/close cycles,
// so reopening the previewer renders instantly without re-hitting the proxy.
export default createImageLoader();
